import type { QueueDelayAlertPayload } from 'types/queueAlert.type';

/** One decimal at most: these are projections, and trailing precision reads as false accuracy. */
const formatDecimal = (value: number): string => value.toLocaleString('en-US', { maximumFractionDigits: 1 });

/**
 * The queueDelayAlert socket frame. Every field is optional: the toast is a notification, so a
 * frame the backend changes shape on should still say something useful rather than throw.
 */
export type QueueDelayAlertMessage = {
    message?: string;
    data?: {
        alertId?: string;
        alertType?: string;
        workflowId?: string;
        workflowTitle?: string;
        triggeredAt?: string;
        payload?: Partial<QueueDelayAlertPayload>;
    };
};

/**
 * Toast copy for a queue delay alert. Leads with the campaign, because the alert fans out to
 * every manager of the tenant and is not scoped to whatever they happen to be looking at.
 */
export const formatQueueDelayAlertToast = (message: QueueDelayAlertMessage): string => {
    const title = message.data?.workflowTitle?.trim();
    const heading = title ? `Queue delay: ${title}` : 'Queue delay alert';

    const payload = message.data?.payload;
    const pendingOverdue = payload?.pendingOverdue;
    const atRiskPct = payload?.atRiskPct;

    if (typeof pendingOverdue === 'number' && typeof atRiskPct === 'number') {
        const calls = pendingOverdue === 1 ? 'call' : 'calls';
        return `${heading}. ${pendingOverdue} overdue ${calls}, ${Math.round(atRiskPct)}% not projected to clear today.`;
    }

    const backendMessage = message.message?.trim();
    return backendMessage ? `${heading}. ${backendMessage}` : heading;
};

export const formatCapacityPerHour = (value: number): string => `${formatDecimal(value)} calls/hour`;

export const formatTimeRemaining = (hours: number): string =>
    hours <= 0 ? 'Business day closed' : `${formatDecimal(hours)}h left today`;

export const formatAtRiskPct = (value: number): string => `${Math.round(value)}%`;

/**
 * One-line explanation of why an alert is open, shared by the detail-page banner and the
 * command center strip so the two never describe the same alert differently.
 */
export const formatQueueDelayAlertSummary = (payload: QueueDelayAlertPayload): string => {
    const calls = payload.pendingOverdue === 1 ? 'call' : 'calls';
    return (
        `${payload.pendingOverdue} overdue follow-up ${calls}: ${formatAtRiskPct(payload.atRiskPct)} are not ` +
        `projected to clear before the end of the business day, above the ${payload.thresholdPct}% alert threshold.`
    );
};
